"use client";

import React, { useEffect, useRef, useState } from "react";
import { AiOutlineDownload } from "react-icons/ai";
import type { ProblemSetVisibilityEnum } from "~/lib/objects";
import { useMistakeReviewIdentity } from "./identity";
import { fetchMistakeReviewAvailability } from "./api";
import MistakeReviewDialog from "./MistakeReviewDialog";

export type MistakeReviewSource = Readonly<{
  problemSetId: number;
  name: string;
  visibility: ProblemSetVisibilityEnum;
}>;

type Props = {
  source: MistakeReviewSource;
  historyVersion?: unknown;
  onDownload: () => void;
  onCreated?: () => void;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
};

export default function ProblemSetActions(props: Props) {
  const userId = useMistakeReviewIdentity();
  // Discard all source/user-owned state, including in-flight response handlers.
  return (
    <SourceActions
      key={`${props.source.problemSetId}:${userId ?? ""}:${props.source.name}:${props.source.visibility}`}
      {...props}
    />
  );
}

function SourceActions({
  source,
  historyVersion,
  onDownload,
  onCreated,
  ...props
}: Props) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    let current = true;
    const controller = new AbortController();
    setAvailable(false);
    setLoading(true);
    setError(undefined);
    void (async () => {
      try {
        const available = await fetchMistakeReviewAvailability(
          source.problemSetId,
          controller.signal,
        );
        if (current) setAvailable(available);
      } catch (cause) {
        if (current)
          setError(
            cause instanceof Error
              ? cause.message
              : "Не удалось проверить доступность.",
          );
      } finally {
        if (current) setLoading(false);
      }
    })();
    return () => {
      current = false;
      controller.abort();
    };
  }, [open, source.problemSetId, historyVersion]);

  useEffect(() => {
    if (!open) return;
    root.current
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
      ?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  return (
    <div
      ref={root}
      className="relative"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        ref={trigger}
        type="button"
        className={props.className ?? "rounded p-1 hover:bg-muted/50"}
        aria-label={props["aria-label"] ?? "Действия с набором"}
        data-testid={props["data-testid"]}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <AiOutlineDownload />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Действия с набором"
          className="absolute right-0 top-full z-20 mt-2 min-w-64 rounded-md border bg-background p-1 shadow-lg"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              setOpen(false);
              trigger.current?.focus();
            }
            if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
              event.preventDefault();
              const items = Array.from(
                event.currentTarget.querySelectorAll<HTMLButtonElement>(
                  '[role="menuitem"]',
                ),
              );
              const index = items.indexOf(
                document.activeElement as HTMLButtonElement,
              );
              const next =
                event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? items.length - 1
                    : (index +
                        (event.key === "ArrowDown" ? 1 : -1) +
                        items.length) %
                      items.length;
              items[next]?.focus();
            }
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-accent"
            onClick={() => {
              setOpen(false);
              onDownload();
            }}
          >
            Скачать задачи
          </button>
          {loading && (
            <p role="status" className="px-3 py-2 text-sm">
              Проверка доступности…
            </p>
          )}
          {error && (
            <p role="alert" className="px-3 py-2 text-sm">
              {error}
            </p>
          )}
          {!loading && available && (
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-accent"
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
            >
              Работа над ошибками
            </button>
          )}
        </div>
      )}
      {creating && (
        <MistakeReviewDialog
          source={source}
          onCreated={onCreated}
          onClose={() => {
            setCreating(false);
            trigger.current?.focus();
          }}
        />
      )}
    </div>
  );
}
